/**
 * Flatten an error's `cause` chain into a readable single string.
 *
 * Library errors routinely wrap the useful one: Drizzle reports
 * `Failed query: SELECT 1` and hides the driver's `no pg_hba.conf entry …` in
 * `cause`. Logging only `error.message` therefore says what failed but never why.
 *
 * Returns `undefined` when there is no cause to report.
 */
export function describeErrorCauseChain(error: unknown, maxDepth = 5): string | undefined {
    const messages: string[] = [];
    let current: unknown = getCause(error);

    for (let depth = 0; depth < maxDepth && current !== undefined && current !== null; depth++) {
        messages.push(describeError(current));
        current = getCause(current);
    }

    return messages.length > 0 ? messages.join(' <- ') : undefined;
}

/** Describe an error including its cause chain, e.g. `Failed query: … <- no pg_hba.conf entry …`. */
export function describeErrorWithCause(error: unknown, maxDepth = 5): string {
    const cause = describeErrorCauseChain(error, maxDepth);
    return cause ? `${describeError(error)} <- ${cause}` : describeError(error);
}

function describeError(error: unknown): string {
    if (error instanceof Error) {
        // Postgres errors carry an SQLSTATE that names the failure class (28000 = invalid authorization).
        const code = (error as { code?: unknown }).code;
        return typeof code === 'string' && code.length > 0
            ? `${error.name}: ${error.message} (code ${code})`
            : `${error.name}: ${error.message}`;
    }

    return String(error);
}

function getCause(error: unknown): unknown {
    if (error instanceof Error && 'cause' in error) {
        return (error as { cause?: unknown }).cause;
    }
    return undefined;
}
